
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schemaData?: object | object[];
  schemaId?: string;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schemaData,
  schemaId = 'default',
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);

    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', canonicalUrl, true);
    if (ogTitle) updateMetaTag('twitter:title', ogTitle);
    if (ogDescription) updateMetaTag('twitter:description', ogDescription);

    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    if (schemaData) {
      // Remove old schema scripts for this page
      document.querySelectorAll(`script[data-schema-id="${schemaId}"]`).forEach(el => el.remove());

      const schemas = Array.isArray(schemaData) ? schemaData : [schemaData];
      schemas.forEach((schema) => {
        const schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        schemaScript.setAttribute('data-schema-id', schemaId);
        schemaScript.textContent = JSON.stringify(schema);
        document.head.appendChild(schemaScript);
      });
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, schemaData, schemaId, location.pathname, siteUrl]);
}
